import React from 'react';
import { Instagram, Mail, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { subscribeNewsletter } from '../services/api';
import ConfirmationDialog from './ConfirmationDialog';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { toast } = useToast();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const result = await subscribeNewsletter(email, language);
      setShowConfirmation(true);
      setEmail('');
    } catch (error) {
      toast({
        title: t.error,
        description: error.message || (language === 'en' ? 'Something went wrong. Please try again.' : 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/nuzha-logo.jpg" 
                alt="NuzhaTrips Logo" 
                className="w-16 h-16 object-contain"
              />
              <h3 className="text-xl font-bold">
                Nuzha<span className="text-orange-500">Trips.xyz</span>
              </h3>
            </div>
            <p className="mb-4 text-gray-300">
              {t.footerDescription}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/nuzhatrips?igsh=MWk5Z291enlwNHliaA==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t.quickLinks}</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="hover:text-orange-500 transition-colors text-left">
                  {t.home}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="hover:text-orange-500 transition-colors text-left">
                  {t.about}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('how')} className="hover:text-orange-500 transition-colors text-left">
                  {t.howItWorks}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('pricing')} className="hover:text-orange-500 transition-colors text-left">
                  {t.pricing}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="hover:text-orange-500 transition-colors text-left">
                  {t.contact}
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t.services}</h4>
            <ul className="space-y-2 text-gray-300">
              <li>{t.flightDeals}</li>
              <li>{t.priceAlerts}</li>
              <li>{t.travelPlanning}</li>
              <li>{t.vipSupport}</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-lg font-bold mb-4">{t.newsletter}</h4>
            <p className="mb-4 text-gray-300">{t.newsletterDesc}</p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder={t.yourEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white text-gray-800"
                required
              />
              <Button 
                type="submit" 
                className="bg-orange-500 hover:bg-orange-600"
                disabled={loading}
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 pt-8 text-center text-gray-300">
          <p>{t.copyright}</p>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        type="newsletter"
      />
    </footer>
  );
};

export default Footer;
